import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import theme from '../../../theme/theme';
import { TableTemplate } from '../../../components/ui/tableTemplate/TableTemplate';
import { TablePaginationBar } from '../../../components/ui/TablePaginationBar';
import { DataTableSearchField } from '../../../components/ui/DataTableSearchField';
import type {
  PaginationMeta,
  SortState,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { DEFAULT_TABLE_PAGE_SIZE, usePaginationState } from '../../../hooks/usePaginationState';
import { useSortState } from '../../../hooks/useSortState';
import { SEARCH_DEBOUNCE_MS } from '../../../shared/constants';
import { useNotification } from '../../notifications/hooks/useNotification';
import { usePermissions } from '../../permissions/usePermissions';
import { useRightsProfiles } from '../hooks/useRightsProfiles';
import { useGetRightsProfiles } from '../hooks/useGetRightsProfiles';
import { useRightsProfileColumns } from '../hooks/useRightsProfileColumns';
import { DeleteProfileDialog } from '../components/DeleteProfileDialog/DeleteProfileDialog';
import type { RightsProfile, RightsProfileSortBy } from '../types/rights-profile.types';

const USER_RIGHTS_BASE = '/my-ngo/user-rights';

/** The server's default order (name ascending), shown as the active sort on arrival. */
const INITIAL_SORT: SortState<RightsProfile> = { sortBy: 'name', sortOrder: 'asc' };

/**
 * List screen for RBAC rights profiles: Name / Description / Users / Actions.
 * Searched, sorted and paginated by the server (HOMCO-1413): the search field
 * filters on the name (`name[like]`), the Name and Users headers sort, and the
 * pagination bar shows the API `meta`. One request per user action: the page is
 * reset at render time (keyed state) and the delete follow-up is decided before
 * reloading — same mechanics as `UsersListPage`.
 * Edit navigates to the profile editor; Delete opens `DeleteProfileDialog`.
 */
export function UserRightsListPage() {
  const { t } = useTranslation(['rightsProfiles', 'common']);
  const navigate = useNavigate();

  const { notifySuccess } = useNotification();
  const { can } = usePermissions();
  const { remove } = useRightsProfiles();
  const [deleteTarget, setDeleteTarget] = useState<RightsProfile | null>(null);

  const { limit, setLimit } = usePaginationState(DEFAULT_TABLE_PAGE_SIZE);
  const { sortState, handleSortChange } = useSortState<RightsProfile>(INITIAL_SORT);
  // Only the Name and Users columns are sortable.
  const sortBy = sortState?.sortBy as RightsProfileSortBy | undefined;
  const sortOrder = sortState?.sortOrder;

  // Debounced name search; a blank field clears the search at once (no wait).
  const [searchInput, setSearchInput] = useState('');
  const trimmedSearch = searchInput.trim();
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(trimmedSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedSearch]);
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (value.trim() === '') setQuery('');
  };

  // Back to page 1 when the search, the sort or the page size changes — derived at
  // render time, so the change sends one request (for page 1), never two.
  const pageKey = `${query}|${sortBy ?? ''}|${sortOrder ?? ''}|${limit}`;
  const [pageState, setPageState] = useState({ page: 1, key: pageKey });
  if (pageState.key !== pageKey) setPageState({ page: 1, key: pageKey });
  const page = pageState.key === pageKey ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ page: next, key: pageKey });

  // The page shows its own load error (Alert below): one message, no extra toast.
  const { data, meta, loading, error, refetch } = useGetRightsProfiles(
    { page, limit, sortBy, sortOrder, name: query },
    { notifyOnError: false },
  );
  const rows = data ?? [];
  const paginationMeta: PaginationMeta = meta ?? {
    total: rows.length,
    page,
    limit,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  };

  // No empty-state message while a page is loading or before the first answer (the
  // previous rows would read as "no profile"), nor under the load-error Alert.
  const hideEmptyState = loading || error !== null || data === null;

  const columns = useRightsProfileColumns({
    onEdit: profile => navigate(`${USER_RIGHTS_BASE}/${profile.id}/edit`),
    onDelete: profile => setDeleteTarget(profile),
  });

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[50],
        height: `calc(100vh - ${theme.toolbar.height}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          background: alpha(theme.palette.primary.main, 0.06),
          color: theme.palette.text.body,
        }}
      >
        <Box
          sx={{
            width: theme.avatar.md,
            height: theme.avatar.md,
            borderRadius: theme.borderRadius.md,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <AdminPanelSettingsOutlinedIcon
            sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: theme.typography.employeeName.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.text.body,
          }}
        >
          {t('rightsProfiles:list.title')}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* Create is gated by rights_profiles.create — the backend refuses POST
            /rights-profiles without it (same check guards the create page). */}
        {can('rights_profiles', 'create') && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            aria-label={t('rightsProfiles:list.create')}
            sx={{ borderRadius: 5, textTransform: 'none' }}
            onClick={event => {
              (event.currentTarget as HTMLElement).blur();
              navigate(`${USER_RIGHTS_BASE}/create`);
            }}
          >
            {t('rightsProfiles:list.create')}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 4,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {t('rightsProfiles:list.loadError')}
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TableTemplate<RightsProfile>
            columns={columns}
            rows={rows}
            striped
            stickyHeader
            fillHeight
            loading={loading}
            sortState={sortState}
            onSortChange={handleSortChange}
            toolbarActions={
              <DataTableSearchField
                value={searchInput}
                onChange={handleSearchChange}
                placeholder={t('rightsProfiles:list.searchPlaceholder')}
                clearLabel={t('rightsProfiles:list.searchClear')}
              />
            }
            emptyStateMessage={
              query ? t('rightsProfiles:list.noSearchResults') : t('rightsProfiles:list.empty')
            }
            errorState={hideEmptyState ? <></> : undefined}
            onRowClick={profile => navigate(`${USER_RIGHTS_BASE}/${profile.id}/edit`)}
            pagination={
              <TablePaginationBar
                meta={paginationMeta}
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            }
          />
        </Box>
      </Box>

      <DeleteProfileDialog
        profile={deleteTarget}
        onConfirm={remove}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          notifySuccess(t('rightsProfiles:delete.success'));
          // Decided before reloading: the only row of a later page was deleted → load
          // the previous page directly, never the emptied one. Uses this render's page
          // and key: if the search or sort changed meanwhile, the key reset wins (page 1).
          if (rows.length === 1 && page > 1) setPage(page - 1);
          else refetch();
        }}
      />
    </Box>
  );
}
